/**
 * Phase-1 mock data for the HALO Device Console.
 * Matches frontend/designs/halodesign.md sample device (failing heartbeat).
 */

/** @returns {Object} Deep-cloned mock console state for one device */
export function createMockHaloConsoleState() {
	return JSON.parse(JSON.stringify(MOCK_STATE));
}

/**
 * Simulate a device write + read-back confirmation.
 * @param {number} [ms=700]
 * @returns {Promise<void>}
 */
export function mockConfirmSave(ms = 700) {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

const MOCK_STATE = {
	tenant: 'Chicago Public Schools',
	online: true,
	attentionCount: 1,
	fleet: [
		{ id: 'mock-1', name: '54785_AUD_W_STAGE_Halo3c', attention: false },
		{ id: 'mock-2', name: '10000_Roaming_02-56', attention: false },
		{ id: 'mock-3', name: '54120_GYM_NORTH', attention: true },
	],
	device: {
		id: 'mock-1',
		name: '54785_AUD_W_STAGE_Halo3c',
		shortName: '54785_AUD_W_STAGE',
		model: 'HALO-3C',
		location: 'West wing · Floor 1 · Auditorium',
		firmware: '2.17.0.3.526-3',
		ip: '10.9.164.41',
		mac: 'B0:B3:53:D1:95:C2',
		serial: '32240314-085',
		uptime: '16.6 mo',
		subnet: '255.255.252.0',
		gateway: '',
		link: '100 Mbps',
		assignment: 'DHCP',
		timezone: 'America/Chicago',
		ntpServer: 'ntp-a.cps.edu',
		ntpEnabled: true,
		clockInSync: true,
	},
	health: {
		cloud: { status: 'ok', title: 'Connected', detail: 'Chicago Public Schools' },
		heartbeat: { status: 'error', title: 'Not reaching server', detail: 'Service Unavailable' },
		primary: { status: 'warn', title: 'Enabled, idle', detail: 'no traffic yet' },
		events: { status: 'ok', title: 'None active', detail: '16 configured' },
	},
	identity: {
		name: '54785_AUD_W_STAGE_Halo3c',
		wing: 'West wing',
		floor: '1',
		room: 'Auditorium',
		st_addr: '',
		desc: 'Auditorium',
		latitude: '',
		longitude: '',
		student_count: '',
	},
	presets: {
		names: [
			'Vaping',
			'Smoking',
			'Security',
			'Environmental - Basic',
			'Environmental - Advanced',
		],
		selected: 'Environmental - Basic',
		mode: 'Overwrite',
	},
	readings: [
		{ key: 'temp', label: 'Temp', value: '75.7', unit: '°F', icon: 'mdi-thermometer' },
		{ key: 'rh', label: 'Humidity', value: '48.7', unit: '%', icon: 'mdi-water-percent' },
		{ key: 'co2', label: 'CO2', value: '524', unit: 'ppm', icon: 'mdi-cloud-outline' },
		{ key: 'tvoc', label: 'TVOC', value: '304', unit: 'ppb', icon: 'mdi-blur' },
		{ key: 'aqi', label: 'AQI', value: '20', unit: 'Good', icon: 'mdi-leaf', good: true },
		{ key: 'no2', label: 'NO2', value: '0.6', unit: 'ppb', icon: 'mdi-atom' },
		{ key: 'co', label: 'CO', value: '2.4', unit: 'ppm', icon: 'mdi-molecule' },
		{ key: 'nh3', label: 'NH3', value: '0.04', unit: 'ppm', icon: 'mdi-flask-outline' },
		{ key: 'pm1', label: 'PM1', value: '2', unit: 'µg/m³', icon: 'mdi-blur-radial' },
		{ key: 'pm25', label: 'PM2.5', value: '2', unit: 'µg/m³', icon: 'mdi-blur-radial' },
		{ key: 'pm10', label: 'PM10', value: '2', unit: 'µg/m³', icon: 'mdi-blur-radial' },
		{ key: 'light', label: 'Light', value: '40.6', unit: 'lux', icon: 'mdi-white-balance-sunny' },
		{ key: 'sound', label: 'Sound', value: '47.4', unit: 'dB', icon: 'mdi-volume-high' },
		{ key: 'pressure', label: 'Pressure', value: '29.8', unit: 'inHg', icon: 'mdi-gauge' },
		{ key: 'health', label: 'Health Index', value: '2', unit: 'Moderate', icon: 'mdi-heart-pulse' },
		{ key: 'motion', label: 'Motion', value: '17', unit: '', icon: 'mdi-motion-sensor' },
	],
	integrations: {
		activeTab: 'heartbeat',
		primary: {
			enabled: true,
			status: 'warn',
			statusLabel: 'Enabled, idle',
			host: 'ingest.hawkenaq.com',
			port: '443',
			endpoint: '/api/halo/event',
			protocol: 'HTTP',
			retryHoldoff: '5',
			interval: null,
			authMode: 'Basic',
			username: 'halo3c_primary',
			password: '••••••••',
			bearerToken: '',
			bearerAuthUrl: '',
			oauthAuthUrl: '',
			refreshToken: '',
			refreshTokenKey: '',
			error: null,
		},
		secondary: {
			enabled: false,
			status: 'off',
			statusLabel: 'Disabled',
			host: '',
			port: '443',
			endpoint: '/api/halo/event',
			protocol: 'HTTP',
			retryHoldoff: '5',
			interval: null,
			authMode: 'Basic',
			username: '',
			password: '',
			bearerToken: '',
			bearerAuthUrl: '',
			oauthAuthUrl: '',
			refreshToken: '',
			refreshTokenKey: '',
			error: null,
		},
		heartbeat: {
			enabled: true,
			status: 'error',
			statusLabel: 'Error',
			host: 'ingest.hawkenaq.com',
			port: '443',
			endpoint: '/api/halo/hb',
			protocol: 'HTTP',
			retryHoldoff: '5',
			interval: '60',
			authMode: 'Basic',
			username: 'halo3c_hb',
			password: '••••••••',
			bearerToken: '',
			bearerAuthUrl: '',
			oauthAuthUrl: '',
			refreshToken: '',
			refreshTokenKey: '',
			error: "The server isn't accepting heartbeats. Device config is valid, but /api/halo/hb returned Service Unavailable. Check the ingest endpoint, not the device.",
		},
	},
	events: {
		configured: 16,
		enabled: 15,
		groups: [
			{
				key: 'safety',
				label: 'Safety',
				tone: 'danger',
				items: [
					{ id: 'gs', name: 'Gunshot', source: 'audsensor/gs', threshold: 50, enabled: true },
					{ id: 'agg', name: 'Aggression', source: 'audsensor/rms0', threshold: 20, enabled: true },
					{ id: 'help', name: 'Help', source: 'audsensor/kw1', threshold: 50, enabled: true },
					{ id: 'tamper', name: 'Tamper', source: 'accsensor/move', threshold: 1400, enabled: true },
					{ id: 'motion', name: 'Motion', source: 'pir/max', threshold: 50, enabled: true },
				],
			},
			{
				key: 'detection',
				label: 'Detection',
				tone: 'warn',
				items: [
					{ id: 'vape', name: 'Vape', source: 'pmsensor/raw/2', threshold: 35, enabled: true },
					{ id: 'thc', name: 'THC', source: 'pmsensor/raw/2', threshold: 35, enabled: true },
					{ id: 'mask', name: 'Masking', source: 'pmsensor/raw/2', threshold: 35, enabled: true },
				],
			},
			{
				key: 'environment',
				label: 'Environment',
				tone: 'ok',
				items: [
					{ id: 'aqi', name: 'AQI', source: 'aqi', threshold: 51, enabled: true },
					{ id: 'pm25', name: 'PM2.5', source: 'pm25', threshold: 51, enabled: true },
					{ id: 'tvoc', name: 'TVOC', source: 'tvoc', threshold: 1000, enabled: true },
					{ id: 'co2', name: 'CO2cal', source: 'co2', threshold: 1000, enabled: true },
					{ id: 'rh', name: 'Humidity', source: 'rh', threshold: 60, enabled: true },
					{ id: 'temp', name: 'Temp_F', source: 'temp', threshold: 80, enabled: true },
					{ id: 'no2', name: 'NO2', source: 'no2', threshold: 101, enabled: false },
					{ id: 'hi', name: 'Health_Index', source: 'hi', threshold: 3, enabled: true },
				],
			},
		],
	},
	sensors: {
		tempOffsetC: '0',
		volumeDb: '120',
		micLevelL: '27',
		micLevelR: '12',
		micGainL: '1',
		micGainR: '0',
		micDbOffset: '50',
		amplifyEnabled: false,
		amplifyNote: 'No beacons paired.',
	},
	advanced: {
		bacnet: {
			instance: '1002021',
			port: '47814',
			deviceName: '54785_AUD_W_STAGE_Halo3c',
			covIncrement: '5',
			location: '',
			foreignDevice: false,
			foreignBBMD: '',
			foreignTTL: '30',
		},
		snmp: {
			enabled: false,
			community: 'private',
			username: 'cpsnmp',
			rocommunity: 'public',
			rousername: 'user',
			location: '',
			contact: '',
		},
		web: {
			httpsPort: '443',
			realm: 'IPV_HALO',
			redirectHttp: true,
		},
	},
	protocolOptions: ['HTTP', 'TCP', 'HTTPS'],
	authModeOptions: ['Basic', 'Bearer', 'OAuth2'],
	presetModeOptions: ['Overwrite', 'Merge', 'Keep'],
	assignmentOptions: ['Unknown', 'DHCP', 'Static'],
};
